import { useEffect, useMemo, useState, type ReactNode } from "react";
import { toast } from "sonner";

import { ApiError } from "@/lib/api";
import { useInitData } from "@/lib/init-data";
import {
  fetchMyRequestFilterOptions,
  REQ_TYPE_INTERNAL_TRANSFER_ID,
  STATUS_FILTERS,
  type MyRequestFilterOptions,
  type MyRequestFilters,
} from "@/lib/payreq";
import { Button } from "@/components/ui/button";
import { Combobox, type ComboboxOption } from "@/components/ui/combobox";
import { DatePicker } from "@/components/ui/date-picker";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Sheet,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";

/** Fallback when init data hasn't loaded (Constants.PAYMENT_REQUEST_TYPE_PAYMENT_REQUEST_ID). */
const REQ_TYPE_PAYMENT_REQUEST_ID = "541";

/**
 * Draft (unapplied) state of the drawer. Ids/dates mirror MyRequestFilters; amounts are the
 * raw input strings; reqTypeId/status are select values ("" = all).
 */
interface Draft {
  dateFrom: string;
  dateTo: string;
  vendorId: number | null;
  reqTypeId: string;
  financialYearId: number | null;
  budgetItemId: number | null;
  amountMin: string;
  amountMax: string;
  status: string;
  requesterDevoteeId: number | null;
  approverDevoteeId: number | null;
}

function toDraft(applied: MyRequestFilters, status: string | null): Draft {
  return {
    dateFrom: applied.dateFrom ?? "",
    dateTo: applied.dateTo ?? "",
    vendorId: applied.vendorId ?? null,
    reqTypeId: applied.reqTypeId != null ? String(applied.reqTypeId) : "",
    financialYearId: applied.financialYearId ?? null,
    budgetItemId: applied.budgetItemId ?? null,
    amountMin: applied.amountMin != null ? String(applied.amountMin) : "",
    amountMax: applied.amountMax != null ? String(applied.amountMax) : "",
    status: status ?? "",
    requesterDevoteeId: applied.requesterDevoteeId ?? null,
    approverDevoteeId: applied.approverDevoteeId ?? null,
  };
}

const EMPTY_DRAFT: Draft = toDraft({}, null);

function parseAmount(s: string): number | null {
  const t = s.trim();
  if (!t) return null;
  const n = Number(t);
  return Number.isFinite(n) ? n : null;
}

function toFilters(d: Draft): MyRequestFilters {
  const f: MyRequestFilters = {};
  if (d.dateFrom) f.dateFrom = d.dateFrom;
  if (d.dateTo) f.dateTo = d.dateTo;
  if (d.vendorId != null) f.vendorId = d.vendorId;
  if (d.reqTypeId) f.reqTypeId = Number(d.reqTypeId);
  if (d.financialYearId != null) f.financialYearId = d.financialYearId;
  if (d.budgetItemId != null) f.budgetItemId = d.budgetItemId;
  const min = parseAmount(d.amountMin);
  const max = parseAmount(d.amountMax);
  if (min != null) f.amountMin = min;
  if (max != null) f.amountMax = max;
  if (d.requesterDevoteeId != null) f.requesterDevoteeId = d.requesterDevoteeId;
  if (d.approverDevoteeId != null) f.approverDevoteeId = d.approverDevoteeId;
  return f;
}

function comboOptions(list: { id: number; name: string | null }[] | undefined): ComboboxOption[] {
  return (list ?? []).map((o) => ({ id: o.id, name: o.name ?? String(o.id) }));
}

const AMOUNT_RE = /^\d*\.?\d{0,2}$/;

/**
 * Bottom drawer with the advanced list filters. Purely draft state — nothing takes effect
 * until Apply, which hands the parent the parsed filters plus the (chip-synced) status.
 * Options are fetched lazily on first open.
 */
export function RequestFiltersSheet({
  open,
  onOpenChange,
  token,
  applied,
  status,
  onApply,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  token: string | null;
  applied: MyRequestFilters;
  status: string | null;
  onApply: (filters: MyRequestFilters, status: string | null) => void;
}) {
  const { reqTypePaymentRequestId, reqTypeInternalTransferId } = useInitData();

  const [draft, setDraft] = useState<Draft>(EMPTY_DRAFT);
  const [options, setOptions] = useState<MyRequestFilterOptions | null>(null);
  const [optionsLoading, setOptionsLoading] = useState(false);

  // Reopening shows the currently applied values, discarding any unapplied edits.
  useEffect(() => {
    if (open) setDraft(toDraft(applied, status));
  }, [open, applied, status]);

  // Load the dropdown options lazily on first open (a failure retries on the next open).
  useEffect(() => {
    if (!open || options || optionsLoading || !token) return;
    setOptionsLoading(true);
    fetchMyRequestFilterOptions(token)
      .then(setOptions)
      .catch((e) => {
        toast.error(e instanceof ApiError ? e.message : "Could not load filter options.");
      })
      .finally(() => setOptionsLoading(false));
  }, [open, options, optionsLoading, token]);

  const patch = (p: Partial<Draft>) => setDraft((d) => ({ ...d, ...p }));

  const min = parseAmount(draft.amountMin);
  const max = parseAmount(draft.amountMax);
  const amountError = min != null && max != null && min > max ? "Min must be ≤ max." : null;

  const vendorOptions = useMemo(() => comboOptions(options?.vendors), [options]);
  const budgetOptions = useMemo(() => comboOptions(options?.budgetItems), [options]);
  const requesterOptions = useMemo(() => comboOptions(options?.requesters), [options]);
  const approverOptions = useMemo(() => comboOptions(options?.approvers), [options]);

  const typeItems = [
    { value: "", label: "All types" },
    { value: reqTypePaymentRequestId ?? REQ_TYPE_PAYMENT_REQUEST_ID, label: "Vendor payment" },
    { value: reqTypeInternalTransferId ?? REQ_TYPE_INTERNAL_TRANSFER_ID, label: "Internal transfer" },
  ];
  const yearItems = [
    { value: "", label: "All years" },
    ...(options?.financialYears ?? []).map((y) => ({
      value: String(y.id),
      label: y.name ?? String(y.id),
    })),
  ];
  const statusItems = [
    { value: "", label: "All statuses" },
    ...STATUS_FILTERS.filter((f) => f.value != null).map((f) => ({
      value: f.value as string,
      label: f.label,
    })),
  ];

  const loadingHint = optionsLoading && !options;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="bottom" className="max-h-[80dvh] gap-0">
        <SheetHeader className="border-b">
          <SheetTitle>Filters</SheetTitle>
        </SheetHeader>

        <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto p-4 [&>*]:shrink-0">
          {loadingHint && (
            <p className="text-xs text-muted-foreground">Loading filter options…</p>
          )}

          <div className="grid grid-cols-2 gap-3">
            <Field label="From date">
              <DatePicker
                value={draft.dateFrom || undefined}
                onChange={(v) => patch({ dateFrom: v })}
                placeholder="Any"
              />
            </Field>
            <Field label="To date">
              <DatePicker
                value={draft.dateTo || undefined}
                onChange={(v) => patch({ dateTo: v })}
                placeholder="Any"
              />
            </Field>
          </div>

          <Field label="Vendor">
            <Combobox
              options={vendorOptions}
              value={draft.vendorId}
              onValueChange={(id) => patch({ vendorId: id })}
              placeholder={loadingHint ? "Loading…" : "Any vendor"}
              disabled={loadingHint}
            />
          </Field>

          <Field label="Type">
            <StringSelect
              items={typeItems}
              value={draft.reqTypeId}
              onChange={(v) => patch({ reqTypeId: v })}
            />
          </Field>

          <Field label="Financial year">
            <StringSelect
              items={yearItems}
              value={draft.financialYearId != null ? String(draft.financialYearId) : ""}
              onChange={(v) => patch({ financialYearId: v ? Number(v) : null })}
              disabled={loadingHint}
            />
          </Field>

          <Field label="Budget item">
            <Combobox
              options={budgetOptions}
              value={draft.budgetItemId}
              onValueChange={(id) => patch({ budgetItemId: id })}
              placeholder={loadingHint ? "Loading…" : "Any budget item"}
              disabled={loadingHint}
            />
          </Field>

          <Field
            label="Amount"
            hint="Set both to the same value for an exact amount."
            error={amountError}
          >
            <div className="grid grid-cols-2 gap-3">
              <Input
                inputMode="decimal"
                placeholder="Min"
                aria-label="Minimum amount"
                value={draft.amountMin}
                aria-invalid={amountError ? true : undefined}
                onChange={(e) => {
                  if (AMOUNT_RE.test(e.target.value)) patch({ amountMin: e.target.value });
                }}
              />
              <Input
                inputMode="decimal"
                placeholder="Max"
                aria-label="Maximum amount"
                value={draft.amountMax}
                aria-invalid={amountError ? true : undefined}
                onChange={(e) => {
                  if (AMOUNT_RE.test(e.target.value)) patch({ amountMax: e.target.value });
                }}
              />
            </div>
          </Field>

          <Field label="Status">
            <StringSelect
              items={statusItems}
              value={draft.status}
              onChange={(v) => patch({ status: v })}
            />
          </Field>

          <Field label="Requester">
            <Combobox
              options={requesterOptions}
              value={draft.requesterDevoteeId}
              onValueChange={(id) => patch({ requesterDevoteeId: id })}
              placeholder={loadingHint ? "Loading…" : "Any requester"}
              disabled={loadingHint}
            />
          </Field>

          <Field label="Approver">
            <Combobox
              options={approverOptions}
              value={draft.approverDevoteeId}
              onValueChange={(id) => patch({ approverDevoteeId: id })}
              placeholder={loadingHint ? "Loading…" : "Any approver"}
              disabled={loadingHint}
            />
          </Field>
        </div>

        <SheetFooter className="flex-row border-t">
          <Button
            variant="ghost"
            className="flex-1"
            onClick={() => setDraft({ ...EMPTY_DRAFT })}
          >
            Clear all
          </Button>
          <Button
            className="flex-1"
            disabled={amountError != null}
            onClick={() => {
              onApply(toFilters(draft), draft.status || null);
              onOpenChange(false);
            }}
          >
            Apply
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}

function Field({
  label,
  hint,
  error,
  children,
}: {
  label: string;
  hint?: string;
  error?: string | null;
  children: ReactNode;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <Label>{label}</Label>
      {children}
      {error ? (
        <p className="text-xs text-destructive">{error}</p>
      ) : hint ? (
        <p className="text-xs text-muted-foreground">{hint}</p>
      ) : null}
    </div>
  );
}

/** Small full-width select over {value, label} string items ("" = the "All …" entry). */
function StringSelect({
  items,
  value,
  onChange,
  disabled,
}: {
  items: { value: string; label: string }[];
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
}) {
  return (
    <Select
      items={items}
      value={value}
      onValueChange={(v) => onChange(v ? String(v) : "")}
      disabled={disabled}
    >
      <SelectTrigger className="w-full">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {items.map((o) => (
          <SelectItem key={o.value} value={o.value}>
            {o.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
